import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileSpreadsheet, Info, RefreshCw } from 'lucide-react';
import { useSession } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { Button } from '@/shared/components/ui/button';
import { CasaSelector, MonedaToggle } from '@/shared/components/TipoCambioControls';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/money';
import {
  CASAS_DOLAR_LABEL,
  type CasaDolar,
  type VistaMoneda,
} from '@/shared/lib/tipoCambio';
import { listResumenCajas, type ResumenCajaRow } from './api';
import { isNovaCaja, isCajaDiferencia } from './lib/permissions';
import { CajasResumenTable } from './components/CajasResumenTable';
import { buildResumenTesoreriaWorkbook } from './lib/resumenWorkbook';
import { downloadWorkbook, fechaArchivoHoy } from '@/shared/lib/xlsx';

/**
 * Resumen de cajas — la puerta de entrada de Tesorería (`/tesoreria`).
 * Antes había que elegir una caja en un combo para ver algo; ahora se ven
 * todas de una, y el ojo abre el detalle en `/tesoreria/:caja`.
 *
 * Las cifras salen del server con el MISMO helper que el footer del detalle
 * (`computeSaldoSeries`, agrupado por caja en `buildResumenCajas`), así que
 * la fila y el detalle no pueden discrepar. Son del **período activo**:
 * desde la última Apertura, no del mes calendario.
 *
 * Sólo lectura: todas las escrituras (Ingreso, Egreso, Transferir,
 * Depositar, Cierre) viven en el detalle, donde ya está el gate por caja.
 */
export function TesoreriaPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { profile } = useSession();
  const isNovaRole = profile?.perfil === 'nova';
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;

  // Vista en dólares: sólo AR. Las series son argentinas — en MX no hay con
  // qué convertir, así que los controles ni aparecen.
  const permiteUsd = countryCode === 'AR';
  const [vista, setVista] = useState<VistaMoneda>('local');
  const [casa, setCasa] = useState<CasaDolar>('oficial');
  const vistaEfectiva: VistaMoneda = permiteUsd ? vista : 'local';

  const resumenQ = useQuery({
    queryKey: ['tesoreria', countryCode, 'resumen', vistaEfectiva, casa],
    queryFn: () => listResumenCajas({ vista: vistaEfectiva, casa }),
    staleTime: 30_000,
    // ponytail: 60 s sin Realtime en cash_movements (egress); publicar la tabla si hace falta en vivo.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const cotizacion = resumenQ.data?.cotizacion ?? null;
  // Pidió dólares y el server no pudo traer la serie: las cajas vuelven en
  // moneda local. Hay que decirlo — un tablero que dice "USD" con números en
  // pesos es peor que uno que avisa.
  const usdSinCotizacion = vistaEfectiva === 'usd' && !cotizacion && resumenQ.isSuccess;

  // Refresco por polling (15s), igual que el detalle. Nada de realtime:
  // `cash_movements` no está en la publicación de Supabase, así que el hook
  // abriría un canal que no dispara nunca.
  //
  // Mismo alcance por rol que tenía el combo: Nova ve sólo sus cajas + Caja
  // Diferencia (ahí aterriza su margen). El detalle repite el chequeo para
  // que escribir la URL a mano tampoco sirva de atajo.
  const cajas = useMemo(() => {
    const rows = resumenQ.data?.cajas ?? [];
    if (!isNovaRole) return rows;
    return rows.filter((r) => isNovaCaja(r.caja) || isCajaDiferencia(r.caja));
  }, [resumenQ.data, isNovaRole]);

  // Un total por moneda, nunca uno solo: en AR conviven cajas en pesos y en
  // dólares y sumarlas daría un número sin sentido.
  const totalesPorMoneda = useMemo(() => {
    const map = new Map<ResumenCajaRow['moneda'], number>();
    for (const c of cajas) map.set(c.moneda, (map.get(c.moneda) ?? 0) + c.saldo);
    return [...map.entries()];
  }, [cajas]);

  const exportResumen = async () => {
    const hoy = new Date();
    const wb = buildResumenTesoreriaWorkbook(cajas, {
      pais: activeCountry.name,
      fecha: hoy.toLocaleDateString('es-AR'),
      cotizacion: cotizacion
        ? {
            casa: CASAS_DOLAR_LABEL[cotizacion.casa as CasaDolar] ?? cotizacion.casa,
            fecha: cotizacion.fecha,
            valor: cotizacion.valor,
          }
        : null,
    });
    await downloadWorkbook(wb, `tesoreria_saldos_${fechaArchivoHoy()}.xlsx`);
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
            Tesorería
          </h1>
          <p className="text-sm text-muted-foreground">
            Saldo de cada caja y movimiento del período activo (desde la última
            apertura). Abrí una caja para ver el detalle.
          </p>
        </div>
        {permiteUsd && (
          <>
            <MonedaToggle
              valor={vista}
              monedaLocal="ARS"
              onChange={setVista}
              titleLocal="Cada caja en su moneda"
              titleUsd="Las cajas en pesos convertidas a dólares; las que ya son en dólares no cambian"
            />
            <CasaSelector valor={casa} onChange={setCasa} disabled={vista !== 'usd'} />
          </>
        )}
        <Button
          variant="outline"
          size="icon"
          className="h-12 w-12 shrink-0"
          onClick={() => void exportResumen()}
          disabled={cajas.length === 0}
          aria-label="Exportar a Excel"
          title="Exportar el resumen a Excel"
        >
          <FileSpreadsheet className="h-4 w-4" aria-hidden />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-12 w-12 shrink-0"
          onClick={() => qc.invalidateQueries({ queryKey: ['tesoreria'] })}
          aria-label="Actualizar"
          title="Actualizar"
        >
          <RefreshCw
            className={cn('h-4 w-4', resumenQ.isFetching && 'animate-spin')}
            aria-hidden
          />
        </Button>
      </div>

      {/* Qué cotización se usó. Con el mismo formato de aviso que el resto de
          la app y no como texto suelto: dos personas mirando casas distintas
          comparan números que no son comparables y concluyen que el tablero
          está roto. */}
      {cotizacion && (
        <div className="flex shrink-0 items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            Cajas en pesos convertidas al{' '}
            <strong className="font-semibold">
              {CASAS_DOLAR_LABEL[cotizacion.casa as CasaDolar] ?? cotizacion.casa}
            </strong>{' '}
            del {cotizacion.fecha} · {formatMoney(cotizacion.valor, 'ARS')} por USD.
            Las cajas en dólares se muestran sin convertir.
          </span>
        </div>
      )}
      {usdSinCotizacion && (
        <div className="flex shrink-0 items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            No se pudo traer la cotización, así que cada caja se muestra en su
            propia moneda. Probá de nuevo en un rato.
          </span>
        </div>
      )}

      <CajasResumenTable
        rows={cajas}
        loading={resumenQ.isPending}
        error={resumenQ.isError}
        onView={(caja) => navigate(`/tesoreria/${encodeURIComponent(caja)}`)}
        onRetry={() => resumenQ.refetch()}
      />

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-2xl border bg-card px-4 py-3 shadow-sm">
        <span className="text-xs text-muted-foreground">
          {cajas.length} caja{cajas.length === 1 ? '' : 's'}
        </span>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {totalesPorMoneda.map(([moneda, total]) => (
            <span
              key={moneda}
              className={cn(
                'inline-flex h-9 items-center gap-2 rounded-lg border px-3',
                total < 0 ? 'border-rose-300 bg-rose-50' : 'bg-secondary',
              )}
            >
              <span className="text-muted-foreground">Total {moneda}</span>
              <span
                className={cn(
                  'font-semibold',
                  total < 0 ? 'text-rose-700' : 'text-foreground',
                )}
              >
                {formatMoney(total, moneda)}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
